import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, fmtQty, fmtDT } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Empty, tableWrap, th, td, num } from "@/components/common";

export const qtyByUnit = (items, key = "qty") => {
  const t = {};
  items.forEach((i) => { t[i.uom] = (t[i.uom] || 0) + i[key]; });
  return Object.entries(t).map(([u, v]) => fmtQty(v, u)).join(" · ");
};

function PendingPieces() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/cut-pieces").then((r) => setItems(r.data)); }, []);
  return (
    <div className={tableWrap}>
      <table className="w-full" data-testid="pending-pieces-table">
        <thead><tr>{["Cut Piece", "Bill", "Party", "Brand / Size", "Length", "From Drum", "Cut On"].map((h) => <th key={h} className={th}>{h}</th>)}</tr></thead>
        <tbody>{items.map((p) => (
          <tr key={p.id}><td className={`${td} font-mono`}><Link to={`/drums/${p.id}`} className="hover:underline">{p.drum_code}</Link></td><td className={`${td} font-mono`}>{p.bill_no}</td><td className={td}>{p.party_name}</td>
            <td className={td}>{p.brand} · {p.cable_size}</td><td className={`${td} ${num}`}>{fmtQty(p.current_balance, p.uom)}</td><td className={`${td} font-mono`}>{p.source_drum_code}</td><td className={td}>{fmtDT(p.created_at)}</td></tr>
        ))}</tbody>
      </table>
      {!items.length && <Empty text="No cut pieces awaiting dispatch" />}
    </div>
  );
}

export default function DispatchList() {
  const { can } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/dispatches", { params: { q } }).then((r) => setItems(r.data)); }, [q]);
  return (
    <div>
      <PageHeader title="Dispatch" subtitle="Multi-item dispatch against bills. Each item is deducted exactly once; partial and repeat dispatches per bill are allowed.">
        {can("dispatch:create") && <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={() => nav("/dispatch/new")} data-testid="dispatch-new-btn"><Plus className="h-4 w-4 mr-1" />New dispatch</Button>}
      </PageHeader>
      <Tabs defaultValue="register">
        <TabsList><TabsTrigger value="register" data-testid="dispatch-tab-register">Dispatch register</TabsTrigger><TabsTrigger value="pending" data-testid="dispatch-tab-pending">Pending cut pieces</TabsTrigger></TabsList>
        <TabsContent value="register" className="mt-4">
          <Input placeholder="Search dispatch no, bill, party, vehicle, drum…" value={q} onChange={(e) => setQ(e.target.value)} className="sm:max-w-md mb-4" data-testid="dispatch-search-input" />
          <div className={tableWrap}>
            <table className="w-full" data-testid="dispatch-table">
              <thead><tr>{["Dispatch No", "Date", "Party", "Bill", "Items", "Quantity", "Vehicle", "Handover", "Approved"].map((h) => <th key={h} className={th}>{h}</th>)}</tr></thead>
              <tbody>{items.map((d) => (
                <tr key={d.id} className="hover:bg-muted/40" data-testid={`dispatch-row-${d.dispatch_no}`}>
                  <td className={`${td} font-mono`}><Link to={`/dispatch/${d.id}`} className="hover:underline font-medium" data-testid={`dispatch-link-${d.dispatch_no}`}>{d.dispatch_no}</Link></td>
                  <td className={td}>{d.date}</td><td className={td}>{d.party_name}</td><td className={`${td} font-mono`}>{d.bill_no}</td><td className={`${td} ${num}`}>{d.items.length}</td>
                  <td className={`${td} ${num}`}>{qtyByUnit(d.items)}</td><td className={`${td} font-mono`}>{d.vehicle_no}</td>
                  <td className={td}>{d.handover ? "Signed" : <span className="text-amber-600">Pending</span>}</td><td className={td}>{d.approved_by?.name || "—"}</td>
                </tr>
              ))}</tbody>
            </table>
            {!items.length && <Empty text="No dispatches yet" />}
          </div>
        </TabsContent>
        <TabsContent value="pending" className="mt-4"><PendingPieces /></TabsContent>
      </Tabs>
    </div>
  );
}
